<template>
  <div class="config-problems f-h">
    <div v-if="company && company.id" class="f-h-below-title">
      <!-- Problem types section -->
      <ExpandableSettingsItem
        :title="$t('message.TYPES')"
        default-expand
        use-transition-expand
      >
        <div>
          <Tree
            ref="tree"
            :key="treeKey"
            :items="allTypes"
            editable
            name-key="name"
            children-key="types"
            :add-btn-text="$t('message.ADD_TYPE')"
            @addItem="addType"
            @nameEdited="typeEdited"
            @parentChange="parentChanged"
            @deleteItem="deleteType"
          />
          <button class="add-button" @click="addType()">
            <img src="/images/add.png" alt="add">
            {{ $t("message.ADD_TYPE") }}
          </button>
        </div>
      </ExpandableSettingsItem>

      <!-- Problem hints section -->
      <ExpandableSettingsItem
        ref="hintsItem"
        :title="$t('message.CUSTOM_PROBLEM_HINTS')"
        html-id="cfg-pb-hints"
        default-expand
        no-animation
      >
        <ProblemHintsEdition
          :hints="company.companyProblemHints"
          :problem-sheet="company.companyHintsProblemSheet"
          @hintsUpdated="setParams('companyProblemHints', $event)"
          @problemSheetUpdated="setParams('companyHintsProblemSheet', $event)"
        />
      </ExpandableSettingsItem>
    </div>
  </div>
</template>

<script>
/**
 * Config page to edit all the problem-related company-wide settings
 */

import { cloneDeep } from 'lodash';
import { mapState, mapActions } from 'vuex';
// Vuex
import { SET_COMPANY_PARAM } from '@/store/modules/config/action-types';
import { FETCH_PB_TYPES, UPDATE_PB_TYPE, CREATE_PB_TYPE, DELETE_PB_TYPE } from '@/store/action-types';
import { GET_PB_TYPES } from '@/store/mutation-types';
// Components
import ExpandableSettingsItem from '@/components/expandable-settings-item.vue';
import ProblemHintsEdition from '@/components/problem-hints-edition.vue';
import Tree from '@/components/tree.vue';
// Mixins
import mixinConfigPages from '@/mixins/config-pages.vue';
// Utils
import { calert, findTreeItemComponent, nestedFindItemWithId } from '@/utils/main';

export default {

  name: 'ConfigProblems',

  components: {
    ExpandableSettingsItem,
    ProblemHintsEdition,
    Tree,
  },

  mixins: [mixinConfigPages],

  data() {
    return {
      treeKey: 1,
      allTypes: [],
      // Properties used by other components. See src/views/readme.md
      pageTitle: this.$t('message.CONFIG_PROBLEMS'),
    };
  },

  computed: {
    ...mapState({
      pbTypes: state => state[GET_PB_TYPES.stateKey] || [],
      pbTypesLoading: state => state[GET_PB_TYPES.loadingKey],
    }),
  },

  watch: {
    pbTypes() {
      this.allTypes = cloneDeep(this.pbTypes);
      this.$nextTick(() => {
        this.treeKey++;
      });
    },
  },

  methods: {
    refresh() {
      if (!this.pbTypesLoading) {
        this.FETCH_PB_TYPES();
      }
    },
    addType(parentType = { id: 0 }) {
      this.CREATE_PB_TYPE({ parentType: parentType.id }).then(() => {
        const parent = nestedFindItemWithId(this.allTypes, parentType.id, 'types');
        const types = parent === null ? this.allTypes : parent.types;
        this.$nextTick(() => {
          const type = findTreeItemComponent(
            this.$refs.tree.$refs.children,
            types[types.length - 1],
          );
          type.startEdit();
        });
      });
    },
    deleteType(type) {
      calert(this.$t('message.DELETE_SUBTYPES'), [
        { title: this.$t('message.YES'), action: () => { this.confirmDeleteType(type, true); } },
        { title: this.$t('message.NO'), action: () => { this.confirmDeleteType(type, false); } },
        { title: this.$t('message.CANCEL') },
      ]);
    },
    confirmDeleteType(type, deleteChildren) {
      this.DELETE_PB_TYPE({ id: type.id, deleteChildren });
    },
    typeEdited(type) {
      this.UPDATE_PB_TYPE({ id: type.id, name: type.name });
    },
    /** A type's parent has changed */
    parentChanged(params) {
      if (params.id && params.parent) {
        const child = nestedFindItemWithId(this.allTypes, params.id, 'types');
        this.UPDATE_PB_TYPE({ ...child, parentType: params.parent });
      }
    },
    /** Set company params */
    setParams(field, value) {
      this.$store.dispatch(`config/${SET_COMPANY_PARAM}`, { [field]: value }).then(() => {
        this.$nextTick(() => {
          this.$refs.hintsItem.itemExpand(true);
        });
      });
    },
    ...mapActions({
      FETCH_PB_TYPES,
      UPDATE_PB_TYPE,
      CREATE_PB_TYPE,
      DELETE_PB_TYPE,
    }),
  },

};
</script>

<style lang="scss">

.config-problems {

  .settings-dropdown:last-child {
    margin-bottom: 6em;
  }

  .add-button {
    padding: 10px;
  }
}
</style>
